import React, { useState, useEffect, useCallback } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { createSlug, stringify } from '../../util/urlHelpers';
import { ReactComponent as DashboardIcon } from '../../assets/dashboardIcon.svg';
import {
  ACCOUNT_SETTINGS_PAGES,
  ADMIN_DASHBOARD_PAGES,
  BUSINESS_DASHBOARD_PAGES,
  WORKER_DASHBOARD_PAGES,
} from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import {
  Avatar,
  InlineTextButton,
  Logo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  ListingLink,
  OwnListingLink,
  CustomRadioButton,
  SetInterviewBar,
} from '../../components';
import { TopbarSearchForm } from '../../forms';
import { ReactComponent as UserDefaultIcon } from '../../assets/svg/userDefault.svg';

import css from './TopbarDesktop.module.css';
import { NATIVE_LANG_STORAGE_KEY, languageSelectOptions } from '../../marketplace-custom-config';
import { storedData } from '../../util/localStorageHelper';

const langKeyName = 'topbar-desktop-language';
const TopbarDesktop = props => {
  const {
    className,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    currentUserListing,
    currentUserListingFetched,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    userNativeLang,
    onChangeUserNativeLang,
  } = props;
  const [mounted, setMounted] = useState(false);
  // const nativeLang = storedData(NATIVE_LANG_STORAGE_KEY);
  useEffect(() => {
    setMounted(true);
  }, []);
  const isProvider = currentUser && currentUser?.attributes?.profile.publicData.isProvider;
  const isSuperAdmin = currentUser && currentUser?.attributes?.profile?.publicData?.isSuperAdmin;

  const testSuperAdmin =
    currentUser && currentUser?.id?.uuid === '650c0156-3a65-4f3c-a029-1ef032534299';

  const isProfileVerified =
    currentUser && currentUser?.attributes?.profile?.metadata?.isProfileVerified;

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const { interviewDetails } = currentUser?.attributes?.profile?.privateData || {};

  const classes = classNames(rootClassName || css.root, className);

  const search = (
    <TopbarSearchForm
      className={css.searchLink}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
    />
  );

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.inboxLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      (page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage)) ||
      (page === 'AdminStaffPage' && ADMIN_DASHBOARD_PAGES.includes(currentPage)) ||
      (page === 'ManageListingsPage' && BUSINESS_DASHBOARD_PAGES.includes(currentPage)) ||
      (page === 'WorkerDashboardPage' && WORKER_DASHBOARD_PAGES.includes(currentPage));

    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const handleLangChange = values => {
    const value = values[langKeyName];
    onChangeUserNativeLang(value);
  };

  const workerSignupLink = (
    <NamedLink
      name="SignupAsWorkerPage"
      className={css.signupLink}
      activeClassName={css.activeSignupLink}
    >
      <FormattedMessage id="TopbarDesktop.workerSignup" />
    </NamedLink>
  );
  const businessSignupLink = (
    <NamedLink
      name="SignupAsCompanyPage"
      className={css.signupLink}
      activeClassName={css.activeSignupLink}
    >
      <FormattedMessage id="TopbarDesktop.businessSignup" />
    </NamedLink>
  );
  const loginLink = (
    <NamedLink name="LoginPage" className={css.loginLink} activeClassName={css.activeLoginLink}>
      <FormattedMessage id="TopbarDesktop.login" />
    </NamedLink>
  );

  const languageBtn = (
    <CustomRadioButton
      name={langKeyName}
      onChange={handleLangChange}
      radioValues={Object.values(languageSelectOptions)}
      initialValues={{ [langKeyName]: userNativeLang }}
    // initialValues={{
    //   [langKeyName]: Object.keys(nativeLang).length === 0 ? userNativeLang : nativeLang,
    // }}
    />
  );

  // console.log('topbar desktop', nativeLang);

  const listingLink =
    authenticatedOnClientSide && currentUserListingFetched && currentUserListing ? (
      <ListingLink
        className={css.createListingLink}
        activeClassName={css.activeCreateListingLink}
        listing={currentUserListing}
        children={
          <span className={css.createListing}>
            <FormattedMessage id="TopbarDesktop.viewListing" />
          </span>
        }
      />
    ) : null;

  const createListingLink =
    isAuthenticatedOrJustHydrated && !(currentUserListingFetched && !currentUserListing) ? null : (
      <NamedLink className={css.createListingLink} name="NewListingPage">
        <span className={css.createListing}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
    );

  const HomeLink =
    authenticatedOnClientSide && isSuperAdmin ? (
      <NamedLink
        className={css.createListingLink}
        name="AdminStaffPage"
        activeClassName={css.activeCreateListingLink}
      >
        <FormattedMessage id="TopbarDesktop.home" />
      </NamedLink>
    ) : authenticatedOnClientSide && isProvider ? (
      <NamedLink
        className={css.createListingLink}
        name="ManageListingsPage"
        activeClassName={css.activeCreateListingLink}
      >
        <FormattedMessage id="TopbarDesktop.home" />
      </NamedLink>
    ) : authenticatedOnClientSide && !isProvider ? (
      <NamedLink
        className={css.createListingLink}
        activeClassName={css.activeCreateListingLink}
        // name="SearchPage"
        name="SearchPage"
        to={{ search: stringify({ tab: 'MostRecent' }) }}
      >
        <FormattedMessage id="TopbarDesktop.home" />
      </NamedLink>
    ) : (
      <NamedLink
        className={css.createListingLink}
        activeClassName={css.activeCreateListingLink}
        name="LandingPage"
      >
        <FormattedMessage id="TopbarDesktop.home" />
      </NamedLink>
    );

  const employerLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.createListingLink}
      activeClassName={css.activeCreateListingLink}
      // name="CMSPage"
      name="LookingForStaff"
    // params={{ pageId: 'business' }}
    >
      <FormattedMessage id="TopbarDesktop.lookingForStaff" />
    </NamedLink>
  ) : null;

  const employeeLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.createListingLink}
      activeClassName={css.activeCreateListingLink}
      // name="CMSPage"
      name="LookingForWork"
    // params={{ pageId: 'workers' }}
    >
      <FormattedMessage id="TopbarDesktop.lookingForWork" />
    </NamedLink>
  ) : null;
  const blogLink = (
    <NamedLink
      className={css.createListingLink}
      activeClassName={css.activeCreateListingLink}
      name="CMSPage"
      params={{ pageId: 'blog' }}
    >
      <FormattedMessage id="TopbarDesktop.blog" />
    </NamedLink>
  );

  // const aboutLink = (
  //   <NamedLink
  //     className={css.createListingLink}
  //     activeClassName={css.activeCreateListingLink}
  //     name="CMSPage"
  //     params={{ pageId: 'about' }}
  //   >
  //     <FormattedMessage id="TopbarDesktop.aboutUs" />
  //   </NamedLink>
  // );
  const aboutLink = (
    <NamedLink
      className={css.createListingLink}
      activeClassName={css.activeCreateListingLink}
      name="AboutUs"
      params={{ pageId: 'about' }}
    >
      <FormattedMessage id="TopbarDesktop.aboutus" />
    </NamedLink>
  );
  // const postJobLink = (

  //   <OwnListingLink
  //     listing={currentUserListing}
  //     listingFetched={currentUserListingFetched}
  //     className={css.yourListingsLink}
  //   >
  //     <div>
  //       <span className={css.menuItemBorder} />
  //       {currentUserListing ? (
  //         <FormattedMessage id="TopbarDesktop.editYourListingLink" />
  //       ) : (
  //         <FormattedMessage id="TopbarDesktop.addYourListingLink" />
  //       )}
  //     </div>
  //   </OwnListingLink>
  // );
  const profileMenu = authenticatedOnClientSide ? (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {!!isProvider ? (
          <MenuItem key="ProfilePage">
            <NamedLink
              className={classNames(css.profileSettingsLink, currentPageClass('ProfilePage'))}
              name="ProfilePage"
              params={{ id: currentUser?.id.uuid }}
            >
              <span className={css.menuItemBorder} />

              <FormattedMessage id="TopbarDesktop.profilePageLink" />
            </NamedLink>
          </MenuItem>
        ) : (
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(
                css.profileSettingsLink,
                currentPageClass('ProfileSettingsPage')
              )}
              name="ProfileSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
        )}

        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>

        <MenuItem key="MessagePage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('MessagePage'))}
            name="MessagePage"
          // params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
          >
            {/* <span className={css.inbox}> */}
            <FormattedMessage id="TopbarDesktop.inbox" />
            {/* {notificationDot} */}
            {/* </span> */}
          </NamedLink>
        </MenuItem>

        {/* {isProvider && !testSuperAdmin ? ( */}
        {!testSuperAdmin ? (
          isProvider ? (
            <MenuItem key="ManageListingsPage">
              <NamedLink
                className={classNames(
                  css.profileSettingsLink,
                  currentPageClass('ManageListingsPage')
                )}
                name="ManageListingsPage"
                params={{ id: currentUser?.id.uuid }}
              >
                <FormattedMessage id="LayoutWrapperProfileSettingsSideNav.WorkerDashboardTabTitle" />
              </NamedLink>
            </MenuItem>
          ) : (
            <MenuItem key="SearchPageMostRecent">
              <NamedLink
                className={classNames(css.profileSettingsLink)}
                name="SearchPage"
                to={{ search: stringify({ tab: 'MostRecent' }) }}
              >
                <FormattedMessage id="LayoutWrapperProfileSettingsSideNav.WorkerDashboardTabTitle" />
              </NamedLink>
            </MenuItem>

          )
        ) : (
          <MenuItem key="noListing"> </MenuItem>
        )}
        {isProvider && !testSuperAdmin ? (
          <MenuItem key="InboxPage">
            <NamedLink
              className={classNames(css.profileSettingsLink, currentPageClass('InboxPage'))}
              name="InboxPage"
              params={{ id: currentUser?.id.uuid, tab: 'sales' }}
            >
              <FormattedMessage id="TopbarDesktop.notification" />
            </NamedLink>
          </MenuItem>
        ) : (
          <MenuItem key="noNotifiction"> </MenuItem>
        )}
        {!isProvider && !testSuperAdmin ? (
          <MenuItem key="InboxPage">
            <NamedLink
              className={classNames(css.profileSettingsLink, currentPageClass('InboxPage'))}
              name="InboxPage"
              params={{ id: currentUser?.id.uuid, tab: 'orders' }}
            >
              <FormattedMessage id="TopbarDesktop.notification" />
            </NamedLink>
          </MenuItem>
        ) : (
          <MenuItem key="noNotifiction2"> </MenuItem>
        )}

        {/* <MenuItem key="ProfilePage">
          <NamedLink
            className={classNames(css.profileSettingsLink, currentPageClass('ProfilePage'))}
            name="ProfilePage"
            params={{ id: currentUser?.id.uuid }}
          >
            profile page
          </NamedLink>
        </MenuItem> */}
        {isSuperAdmin ? (
          <MenuItem key="AdminStaffPage">
            <NamedLink
              className={classNames(css.profileSettingsLink, currentPageClass('AdminStaffPage'))}
              name="AdminStaffPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.adminPageLink" />
            </NamedLink>
          </MenuItem>
        ) : (
          <MenuItem key="notAdmin"> </MenuItem>
        )}

        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <UserDefaultIcon className={classNames(css.avatar, css.unauthAvatar)} />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="BusinessSignupPage" className={css.menuItem}>
          {businessSignupLink}
        </MenuItem>
        <MenuItem key="WorkerSignupPage" className={css.menuItem}>
          {workerSignupLink}
        </MenuItem>
        <MenuItem key="LoginPage" className={css.menuItem}>
          {loginLink}
        </MenuItem>
      </MenuContent>
    </Menu>
  );

  const isLoginorSignupPage =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/login' ||
      window.location.pathname === '/signup-worker' ||
      window.location.pathname === '/signup-company');

  return (
    <>
      <nav className={classes}>
        <div className={css.TopbarContent}>
          {isSuperAdmin ? (
            <NamedLink
              className={css.createListingLink}
              name="AdminStaffPage"
              activeClassName={css.activeCreateListingLink}
            >
              <Logo
                format="desktop"
                className={css.logo}
                alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
              />
            </NamedLink>
          ) : isProvider ? (
            <NamedLink
              className={css.logoLink}
              name="ManageListingsPage"
            // to={{ search: stringify({ tab: 'WorkingStaffs' }) }}
            >
              <Logo
                format="desktop"
                className={css.logo}
                alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
              />
            </NamedLink>
          ) : !isProvider ? (
            <NamedLink
              className={css.logoLink}
              // name="SearchPage"
              name="SearchPage"
              to={{ search: stringify({ tab: 'MostRecent' }) }}
            // to={{ search: stringify({ tab: 'BestMatches' }) }}
            >
              <Logo
                format="desktop"
                className={css.logo}
                alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
              />
            </NamedLink>
          ) : (
            <NamedLink className={css.logoLink} name="LandingPage">
              <Logo
                format="desktop"
                className={css.logo}
                alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
              />
            </NamedLink>
          )}

          <div className={css.restLinks}>
            {/* {HomeLink}
            {employerLink}
            {employeeLink}
            {blogLink}
            {aboutLink} */}

            {/* {!testSuperAdmin ? (
              !!isProvider ? (
                <NamedLink className={css.createListingLinkButton} name="ManageListingsPage">
                  <DashboardIcon />
                  <FormattedMessage id="TopbarDesktop.businessDashboard" />
                </NamedLink>
              ) : (
                !isProvider && (
                  <NamedLink
                    className={css.createListingLinkButton}
                    name="WorkerDashboardPage"
                    to={{ search: stringify({ tab: 'currentShifts' }) }}
                  >
                    <DashboardIcon />
                    <FormattedMessage id="TopbarDesktop.businessDashboard" />
                  </NamedLink>
                )
              )
            ) : null} */}
            {isSuperAdmin ? (
              <NamedLink className={css.createListingLinkButton} name={'AdminStaffPage'}>
                <DashboardIcon />
                <FormattedMessage id="TopbarDesktop.businessDashboard" />
              </NamedLink>
            ) : isProvider ? (
              <NamedLink className={css.createListingLinkButton} name={'ManageListingsPage'}>
                <DashboardIcon />
                <FormattedMessage id="TopbarDesktop.businessDashboard" />
              </NamedLink>
            ) : currentUser?.id?.uuid ? (
              <NamedLink
                className={css.createListingLinkButton}
                name={'SearchPage'}
                to={{ search: stringify({ tab: 'MostRecent' }) }}
              >
                <DashboardIcon />
                <FormattedMessage id="TopbarDesktop.businessDashboard" />
              </NamedLink>
            ) : // !isAuthenticated && !isLoginorSignupPage ? (
            //   <NamedLink className={css.createListingLinkButton} name={'LoginPage'}>
            //     <DashboardIcon />
            //     <FormattedMessage id="TopbarDesktop.businessDashboard" />
            //   </NamedLink>
            // ) :
            null}

            {languageBtn}
            {profileMenu}
          </div>
        </div>
      </nav>
      {authenticatedOnClientSide &&
        currentUser &&
        !isProvider &&
        !interviewDetails &&
        !isProfileVerified ? (
        <SetInterviewBar userNativeLang={userNativeLang} currentUser={currentUser} />
      ) : null}
      {/* {true ? (
        <SetInterviewBar userNativeLang={userNativeLang} currentUser={currentUser} />
      ) : null} */}
    </>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  currentUserListing: null,
  currentUserListingFetched: false,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUserListing: propTypes.ownListing,
  currentUserListingFetched: bool,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
};

export default TopbarDesktop;
